import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import { CircularProgress } from "@mui/material";
import RefreshIcon from "../../images/refreshNew.svg";
import CopyImage from "../../images/CopyImage.svg";
import { useTranslation } from "react-i18next";
import { getMLRstatus } from "../../services/apis";
import { emitter, getStatusIcon } from "../../Uitls/Util";
import "../../styles/pages/Banners/BannerManager.scss";
import { formatDateTime } from "../../Uitls/DetailsHelpers";
import { ToastContainer, toast } from "react-toastify";

/* --------------------------------- */
/* Helpers                           */
/* --------------------------------- */

const getOrDash = (v, dash = "-") =>
  v === null || v === undefined || v === "" ? dash : v;

const hasRealValue = (v) =>
  v !== null && v !== undefined && v !== "" && v !== "-";

const joinSizes = (sizesArr) =>
  Array.isArray(sizesArr) && sizesArr.length ? sizesArr.join(", ") : "-";

const normalizeDocId = (docId) => {
  if (docId === null || docId === undefined) return "";
  const normalized = String(docId).trim();
  return normalized && normalized !== "-" ? normalized : "";
};

const isSameDocId = (a, b) => {
  const normalizedA = normalizeDocId(a);
  const normalizedB = normalizeDocId(b);
  return !!normalizedA && !!normalizedB && normalizedA === normalizedB;
};

const resolveAssetReviewStatus = (nextAsset = {}, prevAsset = {}) => {
  const isSameAssetDoc = isSameDocId(
    nextAsset?.veevaDocNumber,
    prevAsset?.veevaDocNumber,
  );
  if (isSameAssetDoc && prevAsset?.reviewStatus) {
    return prevAsset.reviewStatus;
  }
  return nextAsset?.reviewStatus || nextAsset?.mlrStatus || "Not Submitted";
};

const resolveSourceReviewStatus = (nextSource = {}, prevSource = {}) => {
  const isSameSourceDoc = isSameDocId(
    nextSource?.veevaDocNumber,
    prevSource?.veevaDocNumber,
  );
  if (isSameSourceDoc && prevSource?.sourceReviewStatus) {
    return prevSource.sourceReviewStatus;
  }
  return (
    nextSource?.sourceReviewStatus ||
    nextSource?.reviewStatus ||
    nextSource?.mlrStatus ||
    "Not Submitted"
  );
};

const resolveRsdReviewStatus = (nextRsd = {}, prevRsd = {}) => {
  const nextRsdDocId = nextRsd?.rsdDocNumber || nextRsd?.veevaDocNumber;
  const prevRsdDocId = prevRsd?.rsdDocNumber || prevRsd?.veevaDocNumber;
  const isSameRsdDoc = isSameDocId(nextRsdDocId, prevRsdDocId);
  if (isSameRsdDoc && prevRsd?.rsdReviewStatus) {
    return prevRsd.rsdReviewStatus;
  }
  return (
    nextRsd?.rsdReviewStatus ||
    nextRsd?.reviewStatus ||
    nextRsd?.mlrStatus ||
    "Not Submitted"
  );
};

const resolveRsdDocId = (rsd = {}, asset = {}, root = {}) =>
  rsd?.rsdDocNumber ||
  rsd?.veevaDocNumber ||
  root?.rsdDocNumber ||
  root?.rsdVeevaDocNumber ||
  asset?.rsdDocNumber ||
  asset?.rsdVeevaDocNumber ||
  asset?.veevaDocNumber ||
  null;

const hydrateRsdDetails = (root = {}, prevRsd = {}) => {
  const asset = root?.assetDetails || {};
  const explicitRsd = root?.rsdDetails || {};
  const hydratedRsd = {
    ...explicitRsd,
    rsdDocNumber: resolveRsdDocId(explicitRsd, asset, root),
    publishedOn:
      explicitRsd?.publishedOn ||
      root?.rsdPublishedOn ||
      asset?.rsdPublishedOn ||
      null,
    approvedOn:
      explicitRsd?.approvedOn ||
      root?.rsdApprovedOn ||
      asset?.rsdApprovedOn ||
      null,
  };

  return {
    ...hydratedRsd,
    rsdReviewStatus: resolveRsdReviewStatus(hydratedRsd, prevRsd),
  };
};

/* --------------------------------- */
/* Tiny presentational row           */
/* --------------------------------- */

function Row({ label, value, isLink = false }) {
  return (
    <div className="kv-row">
      <div className="kv-label">{label}</div>
      <div className={`kv-value ${isLink ? "kv-link" : ""}`}>
        {value ?? "-"}
      </div>
    </div>
  );
}

/* --------------------------------- */
/* TAB 1: Banner Information         */
/* --------------------------------- */

function BannerInformation({
  assetDetails = {},
  rsdDetails = {},
  onRefreshMLR,
  currentSize,
}) {
  const normalizeSize = (v) => {
    if (v === null || v === undefined) return "";
    const str = String(v).toLowerCase().trim();
    const match = str.match(/(\d+)\s*[x×*]\s*(\d+)/i);
    if (match) {
      return `${match[1]}x${match[2]}`;
    }
    return str;
  };

  const rawSizes = Array.isArray(assetDetails?.size)
    ? [...assetDetails.size]
    : [];

  let orderedSizes = rawSizes;

  if (currentSize && rawSizes.length) {
    const normalizedCurrent = normalizeSize(currentSize);

    const idx = rawSizes.findIndex(
      (s) => normalizeSize(s) === normalizedCurrent,
    );

    if (idx > 0) {
      const clone = [...rawSizes];
      const [match] = clone.splice(idx, 1);
      orderedSizes = [match, ...clone];
    }
  }

  const sizes = orderedSizes.length ? orderedSizes.join(", ") : "-";

  // ----------------------------------------------

  const linkToBanner = window.location.href;

  const promomatsDocId = assetDetails?.veevaDocNumber;
  const rsdDocId = resolveRsdDocId(rsdDetails, assetDetails);

  const reviewStatus =
    assetDetails?.reviewStatus || assetDetails?.mlrStatus || "Not Submitted";
  const rsdReviewStatus =
    rsdDetails?.rsdReviewStatus ||
    rsdDetails?.reviewStatus ||
    rsdDetails?.mlrStatus ||
    reviewStatus;
  const hasDocId = hasRealValue(promomatsDocId);
  const hasRsdDocId = hasRealValue(rsdDocId);
  const copyLinkToClipboard = (textToCopy) => {
    (async () => {
      try {
        await navigator.clipboard.writeText(textToCopy);
        toast.success(
          <div className="notificationText">Successfully copied the link</div>,
        );
      } catch (err) {
        toast.error(
          <div className="notificationText">
            Failed to copy — please try again
          </div>,
        );
      }
    })();
  };
  return (
    <div className="tab-pane-wrap banner-info-tab">
      {/* PromoMats Doc ID */}
      <div className="kv-row">
        <div className="kv-label">PromoMats Doc ID:</div>
        <div className="kv-value kv-docid">
          {hasDocId ? (
            <>
              <Tooltip title={reviewStatus}>
                <img
                  src={getStatusIcon(reviewStatus)}
                  alt="Status Icon"
                  className="status-icon-doc"
                  width={12}
                  height={12}
                />
              </Tooltip>
              <span className="doc-link">{getOrDash(promomatsDocId)}</span>
              <img
                src={RefreshIcon}
                alt=""
                className="refreshIcondetails"
                onClick={() => onRefreshMLR(promomatsDocId, "asset")}
                title="Refresh"
              />
            </>
          ) : (
            <span>{getOrDash(null)}</span>
          )}
        </div>
      </div>

      <Row label="Name:" value={getOrDash(assetDetails?.assetName)} />
      <Row
        label="Type:"
        value={assetDetails?.isAnimated ? "Animated" : "Static"}
      />
      <Row label="Size:" value={sizes} />
      <Row label="Product:" value={getOrDash(assetDetails?.brandName)} />
      <Row label="Country:" value={getOrDash(assetDetails?.marketName)} />

      <div className="kv-row">
        <div className="kv-label">Link:</div>
        <div className="kv-value kv-link d-flex">
          <img
            src={CopyImage}
            alt=""
            className="icon-copy"
            title="Copy link"
            //onClick={() => navigator.clipboard.writeText(linkToBanner)}
            onClick={(e) => copyLinkToClipboard(linkToBanner)}
          />
          <Tooltip title={linkToBanner} placement="top">
            <a href={linkToBanner} target="_blank" rel="noreferrer">
              {linkToBanner}
            </a>
          </Tooltip>
        </div>
      </div>

      {/* RSD Block */}
      <div className="rsd-block">
        <div className="kv-row">
          <div className="kv-label">PromoMats RSD Doc ID:</div>
          <div className="kv-value kv-docid">
            {hasRsdDocId ? (
              <>
                <Tooltip title={rsdReviewStatus}>
                  <img
                    src={getStatusIcon(rsdReviewStatus)}
                    alt="Status Icon"
                    className="status-icon-doc"
                    width={12}
                    height={12}
                  />
                </Tooltip>
                <span className="doc-link">{getOrDash(rsdDocId)}</span>
                <img
                  src={RefreshIcon}
                  alt=""
                  className="refreshIcondetails"
                  onClick={() => onRefreshMLR(rsdDocId, "rsd")}
                  title="Refresh"
                />
              </>
            ) : (
              <span>{getOrDash(null)}</span>
            )}
          </div>
        </div>

        <Row
          label="RSD Published on:"
          value={formatDateTime(
            rsdDetails?.publishedOn || assetDetails?.rsdPublishedOn,
          )}
        />
        <Row
          label="RSD Approved on:"
          value={formatDateTime(
            rsdDetails?.approvedOn || assetDetails?.rsdApprovedOn,
          )}
        />
      </div>
    </div>
  );
}

/* --------------------------------- */
/* TAB 2: Source Banner Information  */
/* --------------------------------- */

function SourceBannerInformation({ sourceDetails = {}, onRefreshMLR }) {
  const hasSource =
    !!sourceDetails &&
    Object.keys(sourceDetails).some((key) => key !== "sourceReviewStatus");
  if (!hasSource) {
    return (
      <div className="tab-pane-wrap">
        <Row label="Banner Copied:" value="No" />
      </div>
    );
  }
  const sizes = joinSizes(sourceDetails?.size);
  const sourceDocId = sourceDetails?.veevaDocNumber;
  const hasSourceDocId = hasRealValue(sourceDocId);

  const sourceReviewStatus =
    sourceDetails?.sourceReviewStatus ||
    sourceDetails?.reviewStatus ||
    sourceDetails?.mlrStatus ||
    "Not Submitted";

  const sourceBannerId = sourceDetails?.id;
  const linkToBanner = sourceBannerId
    ? `${window.location.origin}/banner-editor/${sourceBannerId}`
    : null;

  return (
    <div className="tab-pane-wrap">
      <Row label="Banner Copied:" value="Yes" />

      <div className="kv-row">
        <div className="kv-label">PromoMats Doc ID:</div>
        <div className="kv-value kv-docid">
          {hasSourceDocId ? (
            <>
              <Tooltip title={sourceReviewStatus}>
                <img
                  src={getStatusIcon(sourceReviewStatus)}
                  alt="Status Icon"
                  className="status-icon-doc"
                  width={12}
                  height={12}
                />
              </Tooltip>

              <span className="doc-link">{getOrDash(sourceDocId)}</span>

              <img
                src={RefreshIcon}
                alt=""
                className="refreshIcondetails"
                onClick={() => onRefreshMLR(sourceDocId, "source")}
                title="Refresh"
              />
            </>
          ) : (
            <span>{getOrDash(null)}</span>
          )}
        </div>
      </div>

      <Row label="Name:" value={getOrDash(sourceDetails?.assetName)} />

      <Row
        label="Type:"
        value={sourceDetails?.isAnimated ? "Animated" : "Static"}
      />

      <Row label="Size:" value={sizes} />

      <Row label="Product:" value={getOrDash(sourceDetails?.brandName)} />

      <Row label="Country:" value={getOrDash(sourceDetails?.marketName)} />

      {/* Link row */}
      <div className="kv-row">
        <div className="kv-label">Link:</div>
        <div className="kv-value kv-link d-flex">
          {linkToBanner ? (
            <>
              <img
                src={CopyImage}
                alt=""
                className="icon-copy"
                title="Copy link"
                onClick={() => navigator.clipboard.writeText(linkToBanner)}
              />
              <Tooltip title={linkToBanner} placement="top">
                <a href={linkToBanner} target="_blank" rel="noreferrer">
                  {linkToBanner}
                </a>
              </Tooltip>
            </>
          ) : (
            <span>{getOrDash(null)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- */
/* TAB 3: Created & Publish Info     */
/* --------------------------------- */

function CreatedPublishInformation({ assetDetails = {}, rsdDetails = {} }) {
  return (
    <div className="tab-pane-wrap">
      <Row
        label="Created By:"
        value={getOrDash(assetDetails?.createdByEmail)}
      />
      <Row
        label="Created On:"
        value={formatDateTime(assetDetails?.createdDateTime)}
      />
      <Row
        label="Last Modified By:"
        value={getOrDash(assetDetails?.updatedByEmail)}
      />
      <Row
        label="Last Modified On:"
        value={formatDateTime(assetDetails?.updatedDateTime)}
      />
      <Row label="Published By:" value={getOrDash(rsdDetails?.publishedBy)} />
      <Row
        label="Published On:"
        value={formatDateTime(rsdDetails?.publishedOn)}
      />
    </div>
  );
}

/* --------------------------------- */
/* Main Modal                        */
/* --------------------------------- */

export default function InfoDetailsDialog(props) {
  const { setOpenInfoDetails, bannerTemplateDetails, currentSize } = props;

  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [innerLoading, setInnerLoading] = useState(false);
  const detailsRoot =
    bannerTemplateDetails?.data || bannerTemplateDetails || {};

  const [assetDetails, setAssetDetails] = useState(() => {
    const initialAsset = detailsRoot.assetDetails || {};
    return {
      ...initialAsset,
      reviewStatus: resolveAssetReviewStatus(initialAsset),
    };
  });
  const [rsdDetails, setRsdDetails] = useState(() => {
    return hydrateRsdDetails(detailsRoot);
  });
  const [sourceDetails, setSourceDetails] = useState(() => {
    const initialSource = detailsRoot.sourceAssetDetails || {};
    return {
      ...initialSource,
      sourceReviewStatus: resolveSourceReviewStatus(initialSource),
    };
  });
  const isOneTimeAsset = Boolean(assetDetails?.oneTimeAsset);
  useEffect(() => {
    const root = bannerTemplateDetails?.data || bannerTemplateDetails || {};
    const nextAsset = root.assetDetails || {};
    const nextSource = root.sourceAssetDetails || {};

    setAssetDetails((prev) => ({
      ...nextAsset,
      reviewStatus: resolveAssetReviewStatus(nextAsset, prev),
    }));
    setRsdDetails((prev) => hydrateRsdDetails(root, prev));
    setSourceDetails((prev) => ({
      ...nextSource,
      sourceReviewStatus: resolveSourceReviewStatus(nextSource, prev),
    }));
  }, [bannerTemplateDetails]);

  const handleClose = () => {
    setOpenInfoDetails(false);
    setTab(0);
  };

  const getLatestMLRStatus = (damDocNumber, targetKey = "asset") => {
    const resolvedDamDocNumber = normalizeDocId(damDocNumber);
    if (!resolvedDamDocNumber) return;
    setInnerLoading(true);

    getMLRstatus(resolvedDamDocNumber, async (response, err) => {
      setInnerLoading(false);
      if (err) return;

      const data = await response.data;
      const status = data?.mlrStatus || "Not Submitted";

      if (targetKey === "asset") {
        setAssetDetails((prev) => ({
          ...prev,
          reviewStatus: status,
        }));
      } else if (targetKey === "rsd") {
        setRsdDetails((prev) => ({
          ...prev,
          rsdReviewStatus: status,
        }));
      } else if (targetKey === "source") {
        setSourceDetails((prev) => ({
          ...prev,
          sourceReviewStatus: status,
        }));
      }
    });
  };

  useEffect(() => {
    if (normalizeDocId(assetDetails?.veevaDocNumber)) {
      getLatestMLRStatus(assetDetails.veevaDocNumber, "asset");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetDetails?.veevaDocNumber]);

  useEffect(() => {
    if (normalizeDocId(sourceDetails?.veevaDocNumber)) {
      getLatestMLRStatus(sourceDetails.veevaDocNumber, "source");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceDetails?.veevaDocNumber]);

  useEffect(() => {
    const rsdDocId = resolveRsdDocId(rsdDetails, assetDetails, detailsRoot);
    if (normalizeDocId(rsdDocId)) {
      getLatestMLRStatus(rsdDocId, "rsd");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rsdDetails?.rsdDocNumber,
    rsdDetails?.veevaDocNumber,
    assetDetails?.rsdDocNumber,
    assetDetails?.rsdVeevaDocNumber,
    assetDetails?.veevaDocNumber,
  ]);

  useEffect(() => {
    const handleMLRStatusUpdate = (data) => {
      const eventDocId = normalizeDocId(data?.damDocNumber);
      if (!eventDocId) return;

      if (isSameDocId(eventDocId, assetDetails?.veevaDocNumber)) {
        setAssetDetails((prev) => ({
          ...prev,
          reviewStatus: data.mlrStatus || "Not Submitted",
        }));
      }
      if (isSameDocId(eventDocId, sourceDetails?.veevaDocNumber)) {
        setSourceDetails((prev) => ({
          ...prev,
          sourceReviewStatus: data.mlrStatus || "Not Submitted",
        }));
      }

      const rsdDocId = resolveRsdDocId(rsdDetails, assetDetails, detailsRoot);
      if (isSameDocId(eventDocId, rsdDocId)) {
        setRsdDetails((prev) => ({
          ...prev,
          rsdReviewStatus: data.mlrStatus || "Not Submitted",
        }));
      }
    };

    emitter.on("autoSync", handleMLRStatusUpdate);
    return () => {
      emitter.off("autoSync", handleMLRStatusUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assetDetails?.veevaDocNumber,
    sourceDetails?.veevaDocNumber,
    rsdDetails?.rsdDocNumber,
    rsdDetails?.veevaDocNumber,
    assetDetails?.rsdDocNumber,
    assetDetails?.rsdVeevaDocNumber,
    assetDetails?.veevaDocNumber,
  ]);

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      className="details-modal detilsDialog"
      id="details-modal"
      aria-labelledby="form-dialog-title"
      PaperProps={{ className: "details-dialog-paper" }}
    >
      {innerLoading && (
        <>
          <div className="modal-backdrop fade show" />
          <CircularProgress size="30px" className="customLoader" />
        </>
      )}
      <div className="details-shell">
        <div className="details-header">
          <DialogTitle id="form-dialog-title" className="details-title">
            {t("DETAILS")}
            {/* {isOneTimeAsset && (
              <div className="onetimeTag"> One-time content</div>
            )} */}
          </DialogTitle>
          <Divider className="dividerline" />
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
            className="details-tabs"
          >
            <Tab label={t("Banner Information")} />
            <Tab label={t("Source Banner Information")} />
            <Tab label={t("Created & Publish Information")} />
          </Tabs>
        </div>

        <Divider />

        <div className="details-scroll">
          {tab === 0 && (
            <BannerInformation
              assetDetails={assetDetails}
              rsdDetails={rsdDetails}
              onRefreshMLR={getLatestMLRStatus}
              currentSize={currentSize}
            />
          )}

          {tab === 1 && (
            <SourceBannerInformation
              sourceDetails={sourceDetails}
              onRefreshMLR={getLatestMLRStatus}
            />
          )}

          {tab === 2 && (
            <CreatedPublishInformation
              assetDetails={assetDetails}
              rsdDetails={rsdDetails}
            />
          )}
        </div>

        <DialogActions className="details-footer">
          <Button
            onClick={handleClose}
            className="close-pill"
            disableElevation
            size="small"
          >
            CLOSE
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
}
